import { forwardRef } from 'react';
import { Controller, type Control, type FieldPath, type FieldValues } from 'react-hook-form';
import { TextField, type SxProps, type Theme } from '@mui/material';
import { IMaskInput } from 'react-imask';

// Sort code format: 6 digits with dashes
// Display format: "04-00-04"
const SORT_CODE_MASK = '00-00-00';

const SORT_CODE_DEFINITIONS = {
  0: /[0-9]/,
};

interface SortCodeMaskedInputProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
}

const SortCodeMaskedInput = forwardRef<
  HTMLInputElement,
  SortCodeMaskedInputProps
>(function SortCodeMaskedInput(props, ref) {
  const { onChange, ...other } = props;
  return (
    <IMaskInput
      {...other}
      mask={SORT_CODE_MASK}
      definitions={SORT_CODE_DEFINITIONS}
      inputRef={ref}
      onAccept={(value: string) =>
        onChange({ target: { name: props.name, value } })
      }
      overwrite
    />
  );
});

/**
 * Converts a displayed sort code value (with dashes) to raw format (no dashes).
 * @example sortCodeToRaw('04-00-04') // '040004'
 */
export const sortCodeToRaw = (displayValue: string): string =>
  displayValue.replace(/[\s-]/g, '');

/**
 * Converts a raw sort code value to display format (with dashes).
 * @example sortCodeToDisplay('040004') // '04-00-04'
 */
export const sortCodeToDisplay = (rawValue: string): string => {
  if (!rawValue) return '';
  const clean = rawValue.replace(/[\s-]/g, '');
  if (clean.length !== 6) return rawValue;
  return `${clean.slice(0, 2)}-${clean.slice(2, 4)}-${clean.slice(4)}`;
};

export interface RHFSortCodeInputProps<
  T extends FieldValues,
  TTransformedValues extends FieldValues | undefined = undefined
> {
  /** Field name - must match form schema */
  name: FieldPath<T>;
  /** React Hook Form control object */
  control: Control<T, any, TTransformedValues>;
  /** Field label */
  label?: string;
  /** Helper text to display below the input */
  helperText?: string;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Whether the field is required */
  required?: boolean;
  /** Full width of parent container */
  fullWidth?: boolean;
  /** MUI sx prop for custom styling */
  sx?: SxProps<Theme>;
  /** Data test ID for testing */
  'data-testid'?: string;
}

/**
 * Sort code input with masking, integrated with React Hook Form.
 * Displays as "04-00-04" but stores raw value "040004".
 *
 * @example
 * ```tsx
 * <RHFSortCodeInput
 *   name="sortCode"
 *   control={form.control}
 *   label="Sort Code"
 *   required
 * />
 * ```
 */
export const RHFSortCodeInput = <T extends FieldValues, TTransformedValues extends FieldValues | undefined = undefined>({
  name,
  control,
  label,
  helperText,
  disabled,
  required,
  fullWidth,
  sx,
  'data-testid': testId,
}: RHFSortCodeInputProps<T, TTransformedValues>) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({
        field: { ref, value, onChange, onBlur },
        fieldState: { error },
      }) => (
        <TextField
          value={sortCodeToDisplay(value ?? '')}
          onChange={e => onChange(sortCodeToRaw(e.target.value))}
          onBlur={onBlur}
          inputRef={ref}
          label={label}
          disabled={disabled}
          required={required}
          fullWidth={fullWidth}
          error={!!error}
          helperText={error?.message || helperText}
          sx={sx}
          slotProps={{
            input: {
              inputComponent: SortCodeMaskedInput as never,
            },
            htmlInput: {
              'data-testid': testId,
              inputMode: 'numeric',
            },
          }}
        />
      )}
    />
  );
};
